import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { FiArrowLeft, FiTrendingUp, FiTarget, FiUsers, FiClock, FiStar, FiBookOpen, FiBarChart3, FiAlertCircle } = FiIcons;

const PerformanceManagement = () => {
  const [selectedCycle, setSelectedCycle] = useState('objectives');

  const performanceCycles = {
    objectives: {
      title: 'SMART Objectives Setting',
      subtitle: 'Goal-setting and performance planning',
      timeline: 'Annual/Quarterly',
      cycles: [
        {
          cycle: 'Objective Planning & Cascading',
          urgency: 'high',
          icon: FiTarget,
          actions: [
            '**Cascade organizational objectives** to departmental level',
            'Align **individual objectives** with business strategy',
            'Ensure objectives are **SMART** (Specific, Measurable, Achievable, Relevant, Time-bound)',
            'Set **competency-based objectives** for role development',
            'Include **equality and diversity** objectives where appropriate'
          ],
          compliance: [
            { type: 'acas', text: 'Clear objectives reduce performance disputes and improve motivation' },
            { type: 'legal', text: 'Objective setting must be fair and non-discriminatory' }
          ]
        },
        {
          cycle: 'Objective Setting Meeting',
          urgency: 'high',
          icon: FiUsers,
          actions: [
            'Conduct **collaborative objective-setting** discussion',
            'Agree on **success criteria** and measurement methods',
            'Set **review milestones** throughout the year',
            'Identify **development needs** and support required',
            '**Document agreed objectives** with clear timescales'
          ],
          compliance: [
            { type: 'acas', text: 'Employee involvement in objective setting improves buy-in' },
            { type: 'legal', text: 'Reasonable objectives considering employee capabilities and resources' }
          ]
        },
        {
          cycle: 'Quarterly Review & Adjustment',
          urgency: 'medium',
          icon: FiClock,
          actions: [
            'Conduct **progress review meetings** every quarter',
            'Assess **objective achievement** against milestones',
            '**Adjust objectives** if business priorities change',
            'Provide **ongoing feedback** and coaching support',
            'Document **performance evidence** throughout period'
          ],
          compliance: [
            { type: 'acas', text: 'Regular reviews prevent year-end surprises and support improvement' },
            { type: 'legal', text: 'Fair performance management requires ongoing dialogue' }
          ]
        }
      ]
    },
    appraisals: {
      title: 'Continuous Appraisal System',
      subtitle: 'Regular performance evaluation and feedback',
      timeline: 'Ongoing/Annual',
      cycles: [
        {
          cycle: 'Monthly Check-ins',
          urgency: 'medium',
          icon: FiUsers,
          actions: [
            'Schedule **regular 1:1 meetings** (minimum monthly)',
            'Discuss **current performance** and challenges',
            'Provide **immediate feedback** on achievements',
            'Address **any concerns** or support needs early',
            'Document **key discussion points** and actions'
          ],
          compliance: [
            { type: 'acas', text: 'Regular feedback improves performance and reduces formal issues' },
            { type: 'legal', text: 'Early intervention prevents capability issues escalating' }
          ]
        },
        {
          cycle: 'Mid-Year Review',
          urgency: 'high',
          icon: FiBarChart3,
          actions: [
            'Conduct **formal mid-year assessment** of objective progress',
            'Review **competency development** and training needs',
            'Adjust **performance expectations** if required',
            'Identify **career development** opportunities',
            'Plan **second-half priorities** and focus areas'
          ],
          compliance: [
            { type: 'acas', text: 'Mid-year reviews allow course correction and support planning' },
            { type: 'legal', text: 'Fair assessment requires adequate review periods' }
          ]
        },
        {
          cycle: 'Annual Performance Review',
          urgency: 'critical',
          icon: FiStar,
          actions: [
            'Complete **comprehensive performance evaluation**',
            'Assess **objective achievement** with evidence',
            'Review **competency development** over the year',
            'Discuss **career aspirations** and progression',
            '**Set performance rating** and salary review recommendations'
          ],
          compliance: [
            { type: 'acas', text: 'Annual reviews provide formal record of performance' },
            { type: 'legal', text: 'Performance ratings must be fair, consistent and evidence-based' }
          ]
        },
        {
          cycle: '360-Degree Feedback (Optional)',
          urgency: 'low',
          icon: FiUsers,
          actions: [
            'Collect **multi-source feedback** from peers, reports, customers',
            'Use **structured feedback tools** and anonymous surveys',
            'Facilitate **feedback discussion** sessions',
            'Create **development plans** based on feedback themes',
            'Monitor **behavioral changes** following feedback'
          ],
          compliance: [
            { type: 'acas', text: '360-degree feedback provides comprehensive performance insights' },
            { type: 'legal', text: 'Feedback collection must respect confidentiality and be job-relevant' }
          ]
        }
      ]
    },
    capacity: {
      title: 'Capacity & Workload Management',
      subtitle: 'Monitoring employee wellbeing and productivity',
      timeline: 'Ongoing',
      cycles: [
        {
          cycle: 'Workload Assessment',
          urgency: 'high',
          icon: FiBarChart3,
          actions: [
            'Conduct **regular workload reviews** with each employee',
            'Monitor **working hours** and overtime patterns',
            'Assess **task complexity** and skill requirements',
            'Identify **capacity constraints** and bottlenecks',
            'Track **work-life balance** indicators and stress levels'
          ],
          compliance: [
            { type: 'legal', text: 'Working Time Regulations 1998: Maximum 48-hour working week' },
            { type: 'legal', text: 'Health and Safety at Work Act 1974: Duty to prevent work-related stress' }
          ]
        },
        {
          cycle: 'Stress & Wellbeing Monitoring',
          urgency: 'high',
          icon: FiAlertCircle,
          actions: [
            'Implement **stress risk assessments** for all roles',
            'Monitor **absence patterns** for stress indicators',
            'Conduct **wellbeing surveys** and pulse checks',
            'Provide **Employee Assistance Program** access',
            'Train managers in **mental health awareness**'
          ],
          compliance: [
            { type: 'legal', text: 'Management of Health and Safety at Work Regulations 1999: Risk assessment duty' },
            { type: 'acas', text: 'Proactive wellbeing support reduces absence and improves performance' }
          ]
        },
        {
          cycle: 'Reasonable Adjustments',
          urgency: 'critical',
          icon: FiUsers,
          actions: [
            'Assess **individual adjustment needs** regularly',
            'Implement **workplace modifications** and flexible working',
            'Provide **assistive technology** and equipment',
            'Adjust **performance expectations** where appropriate',
            'Monitor **effectiveness** of adjustments made'
          ],
          compliance: [
            { type: 'legal', text: 'Equality Act 2010: Duty to make reasonable adjustments for disabled employees' },
            { type: 'legal', text: 'Failure to make reasonable adjustments can constitute discrimination' }
          ]
        },
        {
          cycle: 'Performance Improvement Support',
          urgency: 'medium',
          icon: FiTrendingUp,
          actions: [
            'Identify **performance gaps** and root causes',
            'Create **improvement action plans** with clear milestones',
            'Provide **additional training** and coaching support',
            'Adjust **workload distribution** to enable focus',
            'Monitor **progress** and celebrate improvements'
          ],
          compliance: [
            { type: 'acas', text: 'Support before formal capability action demonstrates fairness' },
            { type: 'legal', text: 'Reasonable support required before capability proceedings' }
          ]
        }
      ]
    },
    development: {
      title: 'Training & CPD Management',
      subtitle: 'Continuous professional development tracking',
      timeline: 'Ongoing',
      cycles: [
        {
          cycle: 'Training Needs Analysis',
          urgency: 'medium',
          icon: FiBookOpen,
          actions: [
            'Conduct **annual skills gap analysis** by role and department',
            'Identify **mandatory training** requirements and renewals',
            'Assess **career development** training needs',
            'Map **succession planning** training requirements',
            'Prioritize **training investment** based on business needs'
          ],
          compliance: [
            { type: 'legal', text: 'Health and Safety at Work Act 1974: Adequate training required for safe working' },
            { type: 'acas', text: 'Training investment demonstrates commitment to employee development' }
          ]
        },
        {
          cycle: 'Individual Development Planning',
          urgency: 'high',
          icon: FiTarget,
          actions: [
            'Create **personalized development plans** for each employee',
            'Set **learning objectives** aligned with career goals',
            'Identify **learning methods** (formal, informal, experiential)',
            'Allocate **development time** and budget resources',
            'Schedule **regular progress reviews** on development goals'
          ],
          compliance: [
            { type: 'acas', text: 'Individual development planning improves retention and engagement' },
            { type: 'legal', text: 'Equal access to development opportunities regardless of protected characteristics' }
          ]
        },
        {
          cycle: 'Training Delivery & Tracking',
          urgency: 'high',
          icon: FiClock,
          actions: [
            'Deliver **mandatory training** within required timeframes',
            'Track **training completion** rates and compliance',
            'Monitor **training effectiveness** through assessment',
            'Maintain **training records** for audit and compliance',
            'Evaluate **return on investment** for training programs'
          ],
          compliance: [
            { type: 'legal', text: 'Various regulations require specific training (H&S, GDPR, etc.)' },
            { type: 'legal', text: 'Training records may be required as evidence in tribunal cases' }
          ]
        },
        {
          cycle: 'CPD & Professional Development',
          urgency: 'medium',
          icon: FiStar,
          actions: [
            'Support **professional body** membership and requirements',
            'Fund **external qualifications** and certifications',
            'Provide **conference and networking** opportunities',
            'Facilitate **internal knowledge sharing** sessions',
            'Track **professional development** hours and achievements'
          ],
          compliance: [
            { type: 'acas', text: 'CPD support demonstrates investment in employee career progression' },
            { type: 'legal', text: 'Some roles require mandatory CPD for regulatory compliance' }
          ]
        }
      ]
    }
  };

  const currentCycle = performanceCycles[selectedCycle];

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-600';
      case 'high': return 'bg-orange-600';
      case 'medium': return 'bg-yellow-600';
      case 'low': return 'bg-green-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/" className="mr-4">
          <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Performance Management System</h1>
          <p className="text-gray-600">Comprehensive performance planning, monitoring and development</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.keys(performanceCycles).map((cycle) => (
            <button
              key={cycle}
              onClick={() => setSelectedCycle(cycle)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCycle === cycle 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {cycle === 'objectives' ? 'SMART Objectives' :
               cycle === 'appraisals' ? 'Continuous Appraisals' :
               cycle === 'capacity' ? 'Capacity & Workload' : 'Training & CPD'}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentCycle.title}</h2>
            <p className="text-gray-600">{currentCycle.subtitle}</p>
          </div>
          <div className="text-right">
            <span className="text-sm text-indigo-600 font-medium">Timeline</span>
            <p className="text-lg font-bold text-indigo-800">{currentCycle.timeline}</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {currentCycle.cycles.map((cycle, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className={`${getUrgencyColor(cycle.urgency)} px-6 py-4`}>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={cycle.icon} className="text-white text-2xl" />
                <div>
                  <h3 className="text-white font-semibold text-xl">{cycle.cycle}</h3>
                  <span className="text-white opacity-90 text-sm capitalize">
                    {cycle.urgency} Priority
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiTrendingUp} className="text-indigo-600 mr-2" />
                    Required Actions
                  </h4>
                  <ul className="space-y-3">
                    {cycle.actions.map((action, actionIndex) => (
                      <li key={actionIndex} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0" />
                        <span 
                          className="text-gray-700"
                          dangerouslySetInnerHTML={{ 
                            __html: action.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiAlertCircle} className="text-orange-600 mr-2" />
                    Compliance & Best Practice
                  </h4>
                  <div className="space-y-3">
                    {cycle.compliance.map((requirement, reqIndex) => (
                      <div key={reqIndex} className="flex items-start space-x-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded flex-shrink-0 ${
                          requirement.type === 'legal' 
                            ? 'text-red-600 bg-red-50' 
                            : 'text-green-600 bg-green-50'
                        }`}>
                          {requirement.type === 'legal' ? '⚖️ Legal' : '🟢 ACAS'}
                        </span>
                        <span className="text-gray-700 text-sm">{requirement.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-semibold text-red-900 mb-2">⚖️ Legal Compliance</h4>
          <ul className="text-sm text-red-800 space-y-1">
            <li>• Working time regulations (48-hour limit)</li>
            <li>• Reasonable adjustments (Equality Act)</li>
            <li>• Health & safety training requirements</li>
            <li>• Fair performance management process</li>
          </ul>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-900 mb-2">🟢 Performance Benefits</h4>
          <ul className="text-sm text-green-800 space-y-1">
            <li>• 23% increase in productivity</li>
            <li>• 40% reduction in capability issues</li>
            <li>• 67% improvement in retention</li>
            <li>• Enhanced employee engagement</li>
          </ul>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h4 className="font-semibold text-indigo-900 mb-2">📈 Success Metrics</h4>
          <ul className="text-sm text-indigo-800 space-y-1">
            <li>• Objective completion rates (&gt;85%)</li>
            <li>• Employee satisfaction scores</li>
            <li>• Training completion rates (100%)</li>
            <li>• Career progression tracking</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PerformanceManagement;